import { types, type SnapshotIn } from 'mobx-state-tree';
import Node, { type INode } from './node';
import Link, { type ILink } from './link';
import type { IObjectType } from '../metamodel/object_type';
import type { IDependency } from '../metamodel/dependency';
import type { IInheritance } from '../metamodel/inheritance';

/**
 * The EDG instance interface.
 *
 * HAND-DECLARED (not `Instance<typeof EDG>`) because `Link.edg` reaches the EDG
 * through `getParent(self, 2)`: typing that getter against this interface lets TS
 * resolve it via the EDG *interface* instead of re-inferring `typeof EDG` (which
 * embeds `types.array(Link)` and would loop back into Link's own initializer).
 */
export interface IEDG {
  readonly nodes: INode[];
  readonly links: ILink[];
  findNode(objectType: IObjectType): INode | undefined;
  findNodeById(objectTypeId: string): INode | undefined;
  findLink(dependency: IDependency): ILink | undefined;
  findLinkById(dependencyId: string): ILink | undefined;
  findInheritanceLink(inheritance: IInheritance): ILink | undefined;
  findInheritanceLinkById(inheritanceId: string): ILink | undefined;
  readonly topLeftCoordinates: { x: number; y: number };
  addNode(objectType: string, position?: { x: number; y: number }): INode;
  removeNode(objectTypeId: string): void;
  addLink(dependency: string, vertices?: SnapshotIn<typeof Link>['vertices']): ILink;
  removeLink(dependencyId: string): void;
  addInheritanceLink(inheritance: string, vertices?: SnapshotIn<typeof Link>['vertices']): ILink;
  removeInheritanceLink(inheritanceId: string): void;
  clearAllVertices(): void;
}

type NodeSnap = SnapshotIn<typeof Node>;
type LinkSnap = SnapshotIn<typeof Link>;

/**
 * The EDG in a MERODE ui model.
 */
const EDG = types
  .model('EDG', {
    // nodes: aka the visual counterpart of an object type
    nodes: types.optional(types.array(Node), () => []),
    // links: aka the visual counterpart of a dependency or inheritance
    links: types.optional(types.array(Link), []),
  })
  .views((self) => ({
    // find the node that represents the given object type
    findNode(objectType: IObjectType) {
      return self.nodes.find(
        (p) => p.objectType === (objectType as unknown as typeof p.objectType)
      );
    },
    // same as above, but by id
    findNodeById(objectTypeId: string) {
      return self.nodes.find((n) => n.objectType.id === objectTypeId);
    },
    // find the link that represents the given dependency
    findLink(dependency: IDependency) {
      return self.links.find((l) => l.dependency === dependency);
    },
    // same as above, but by id
    findLinkById(dependencyId: string) {
      return self.links.find((l) => l.dependency != null && l.dependency.id === dependencyId);
    },
    // find the link that represents the given inheritance
    findInheritanceLink(inheritance: IInheritance) {
      return self.links.find((l) => l.inheritance === inheritance);
    },
    // same as above, but by id
    findInheritanceLinkById(inheritanceId: string) {
      return self.links.find((l) => l.inheritance != null && l.inheritance.id === inheritanceId);
    },
    get topLeftCoordinates() {
      const x = Math.min(...self.nodes.map((n) => n.position.x));
      const y = Math.min(...self.nodes.map((n) => n.position.y));
      return {
        x,
        y,
      };
    },
  }))
  .actions((self) => ({
    // objectType is an id! We need this to be serializable to allow replay.
    addNode(objectType: string, position?: NodeSnap['position']) {
      const node = Node.create({ objectType, position } as NodeSnap);
      self.nodes.push(node);
      return node;
    },
    removeNode(objectTypeId: string) {
      const node = self.findNodeById(objectTypeId);
      self.nodes.remove(node as unknown as (typeof self.nodes)[number]);
    },
    // dependency is an id! We need this to be serializable to allow replay.
    addLink(dependency: string, vertices?: LinkSnap['vertices']) {
      const link = Link.create({ dependency, vertices } as LinkSnap);
      self.links.push(link);
      return link;
    },
    removeLink(dependencyId: string) {
      const link = self.findLinkById(dependencyId);
      self.links.remove(link as unknown as (typeof self.links)[number]);
    },
    // inheritance is an id! We need this to be serializable to allow replay.
    addInheritanceLink(inheritance: string, vertices?: LinkSnap['vertices']) {
      const link = Link.create({ inheritance, vertices } as LinkSnap);
      self.links.push(link);
      return link;
    },
    removeInheritanceLink(inheritanceId: string) {
      const link = self.findInheritanceLinkById(inheritanceId);
      self.links.remove(link as unknown as (typeof self.links)[number]);
    },
    // clear the vertices of all links
    clearAllVertices() {
      self.links.forEach((l) => l.clearVertices());
    },
  }));

export default EDG;
